<template>
  <div
    v-observe-visibility="{
      callback: visibilityChanged,
      once: true,
      throttle: 100,
      intersection: {
        rootMargin: '200px 0px'
      }
    }"
    style="width: 100%"
    class="no-page-break-inside"
  >
    <div
      v-if="lazy_load && !isVisible"
      class="chart-placeholder chart-canvas-sb border border-light rounded mt-2 d-flex align-items-center justify-content-center"
      :style="cssVars"
    >
      <b-spinner variant="secondary" label="Loading chart..." />
    </div>
    <canvas
      v-show="(isVisible || !lazy_load) && isRendered"
      :id="`chart_canvas_${chart_id}`"
      ref="qesgChart"
      :style="cssVars"
      class="chart-canvas-sb border border-light rounded mt-2"
    ></canvas>
    <p
      v-if="chartData && chartData.options"
      class="fw-400 f-90 mb-0 text-muted"
      style="white-space: pre-line"
    >
      <span v-if="lang === 'KOR'">
        {{ chartData?.options?.footnote }}
      </span>
      <span v-else>
        {{ chartData_e?.options?.footnote }}
      </span>
    </p>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { mapState, mapGetters } from 'vuex';
import { resolveDatalabelsOptions, inferChartType } from '@/util/chart/datalabels.utils.js';
import { resolveAnnotationOptions } from '@/util/chart/annotation.utils.js';

export default {
  name: 'ChartCanvasSb',
  props: {
    chart_id: { type: Number, default: () => null },
    chartData: { type: Object, default: null },
    chartData_e: { type: Object, default: null },
    lazy_load: { type: Boolean, default: false },
    print_mode: { type: Boolean, default: false }
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('charts', ['chart_data', 'edited_chart_id']),
    main_color() {
      return `color:` + this.selected_report.main_color;
    },
    userChartHeight() {
      return (this.chartData?.options?.chart_height || 400) + 'px';
    },
    cssVars() {
      // 사용자 설정 chart_height을 CSS 변수로 노출 → .chart-canvas-sb 가 min()으로 반응형 캡 적용
      return { '--user-chart-height': this.userChartHeight };
    }
  },
  data() {
    return {
      // chart 인스턴스는 created()에서 non-reactive 로 선언 (Vue Observer 무한 재귀 방지)
      isVisible: false,
      isRendered: false,
      renderTimeout: null
    };
  },
  created() {
    this.chart = null;
  },
  watch: {
    lang: {
      handler() {
        if (this.isVisible || !this.lazy_load) {
          // isRendered 플래그를 false로 설정하여 재렌더링 강제
          this.isRendered = false;
          this.debounceRenderChart();
        }
      }
    }
  },
  mounted() {
    // If lazy_load is disabled, show immediately
    if (this.isVisible || !this.lazy_load) {
      this.isVisible = true;
      this.$nextTick(() => {
        this.debounceRenderChart();
      });
    }
  },
  beforeDestroy() {
    this.cleanupChart();
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
      this.renderTimeout = null;
    }
  },
  methods: {
    formatUnit(context) {
      const unitLabel = this.lang === 'KOR' ? '단위' : 'unit';
      return context.parsed.y !== undefined
        ? `${context.dataset.label}: ${context.parsed.y
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`
        : `${context.dataset.label}: ${context.parsed
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (${unitLabel}: ${context.dataset?.unit})`;
    },

    visibilityChanged(isVisible) {
      if (isVisible && !this.isVisible) {
        this.isVisible = true;
        this.$nextTick(() => {
          this.debounceRenderChart();
        });
      }
    },

    debounceRenderChart() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }
      this.renderTimeout = setTimeout(() => {
        this.renderChart();
      }, 100);
    },

    cleanupChart() {
      if (this.chart) {
        try {
          this.chart.destroy();
        } catch (e) {
          console.warn('Chart destroy error:', e);
        }
        this.chart = null;
      }

      // Canvas 컨텍스트 명시적 정리 (Safari 메모리 최적화)
      if (this.$refs.qesgChart) {
        const canvas = this.$refs.qesgChart;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Safari에서 메모리 해제 강제
          canvas.width = 0;
          canvas.height = 0;
        }
      }
    },

    async renderChart() {
      if ((!this.isVisible && this.lazy_load) || !this.$refs.qesgChart) {
        return;
      }

      // 기존 차트 완전 정리
      this.cleanupChart();

      // 렌더링 전 메모리 정리를 위한 대기
      await new Promise(resolve => setTimeout(resolve, 50));

      try {
        // chart.js plugin(특히 annotation)이 옵션 내부에 chart reference를 박는 경우가 있어
        // props로 받은 reactive 원본을 그대로 넘기면 Observer 무한 재귀가 발생할 수 있다.
        const _source = this.lang !== 'ENG' ? this.chartData : this.chartData_e;
        if (!_source || !_source.options) {
          console.warn('Chart options not available for language:', this.lang);
          return;
        }
        const chartOptions = JSON.parse(JSON.stringify(_source));

        // Safari 메모리 최적화를 위한 설정
        chartOptions.options = {
          ...chartOptions.options,
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0 // Safari에서 애니메이션 비활성화로 메모리 절약
          },
          interaction: {
            intersect: false,
            mode: 'point'
          }
        };

        chartOptions.plugins = [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            }
          },
          ChartDataLabels,
          annotationPlugin
        ];

        const _savedDatalabels = chartOptions.options?.plugins?.datalabels;
        const _savedAnnotations = chartOptions.options?.plugins?.annotations;
        const _chartType = inferChartType(chartOptions);
        chartOptions.options.plugins = {
          ...chartOptions.options.plugins,
          tooltip: {
            intersect: true,
            callbacks: {
              label: this.formatUnit
            }
          },
          datalabels: resolveDatalabelsOptions(_savedDatalabels, _chartType),
          annotation: resolveAnnotationOptions(_savedAnnotations, _chartType)
        };

        // Canvas 크기 제한 (Safari 메모리 최적화 + #663 반응형)
        // - Safari 하드 캡: width 1200px, height 600px
        // - 반응형 캡: --preview-chart-max-height 토큰 (모바일 240/태블릿 320/데스크톱·인쇄 무제한)
        // min() 형태로 지정해 두 캡이 동시에 적용되도록 한다.
        const canvas = this.$refs.qesgChart;
        const maxWidth = Math.min(canvas.parentElement.clientWidth || 800, 1200);
        const safariMaxHeight = Math.min(chartOptions.options.chart_height || 400, 600);

        canvas.style.maxWidth = maxWidth + 'px';
        canvas.style.maxHeight = `min(${safariMaxHeight}px, var(--preview-chart-max-height, 9999px))`;

        if (!canvas) return;
        // 같은 canvas에 stale chart 인스턴스가 남아있으면 "Canvas is already in use" 에러가 나므로 강제 정리
        Chart.getChart(canvas)?.destroy();
        const ctx = canvas.getContext('2d');
        this.chart = new Chart(ctx, chartOptions);
        this.isRendered = true;
      } catch (error) {
        console.error('Chart render error:', error);
        this.isRendered = false;
      }
    }
  }
};
</script>

<style scoped>
.chart-placeholder {
  background-color: #f8f9fa;
}

.chart-placeholder:hover {
  background-color: #e9ecef;
}

/* #663 반응형: 사용자 chart_height과 .report-view의 viewport별 cap 중 작은 값 적용
 * --user-chart-height: 컴포넌트가 inline style로 노출 (기본 400px)
 * --preview-chart-max-height: .report-view 스코프 토큰 (모바일 240/태블릿 320/그 외 9999) */
.chart-canvas-sb {
  max-width: 100%;
}

.chart-placeholder.chart-canvas-sb {
  height: min(var(--user-chart-height, 400px), var(--preview-chart-max-height, 9999px));
}

/* Safari 메모리 최적화를 위한 Canvas 스타일 */
canvas {
  image-rendering: auto;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}
</style>
